import Stripe from 'stripe';
import { supabase } from './supabase';
import { stripeConfig } from './config';

// Initialize Stripe with secret key from config
const stripeSecretKey = stripeConfig.secretKey;

const stripe = new Stripe(stripeSecretKey, {
  apiVersion: '2025-04-30.basil', // Updated to the correct API version
});

// Subscription plan IDs (reference your Stripe Dashboard)
export const SUBSCRIPTION_PLANS = {
  STANDARD: stripeConfig.priceId, // Use price ID from config
};

// Subscription statuses (for reference)
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  PAST_DUE: 'past_due',
  TRIALING: 'trialing',
  UNPAID: 'unpaid',
};

/**
 * Customer Management Functions
 */

// Create a new Stripe customer for a user
export async function createCustomer(email: string, name?: string) {
  try {
    // Handle possible null supabase client
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }
    
    const customer = await stripe.customers.create({
      email,
      name: name || email,
      metadata: {
        supabaseUserId: (await supabase.auth.getUser()).data.user?.id || '',
      },
    });
    return customer;
  } catch (error) {
    console.error('Error creating Stripe customer:', error);
    throw error;
  }
}

// Retrieve a customer from Stripe
export async function getCustomer(customerId: string) {
  try {
    return await stripe.customers.retrieve(customerId);
  } catch (error) {
    console.error('Error retrieving Stripe customer:', error);
    throw error;
  }
}

// Update an existing customer
export async function updateCustomer(customerId: string, data: Stripe.CustomerUpdateParams) {
  try {
    return await stripe.customers.update(customerId, data);
  } catch (error) {
    console.error('Error updating Stripe customer:', error);
    throw error;
  }
}

/**
 * Subscription Management Functions
 */

// Create a new subscription for a customer
export async function createSubscription(
  customerId: string, 
  priceId: string,
  paymentMethodId?: string,
  trialDays?: number
) {
  try {
    // If a payment method is provided, attach it to the customer
    if (paymentMethodId) {
      await stripe.paymentMethods.attach(paymentMethodId, { customer: customerId });
      
      // Set as default payment method
      await stripe.customers.update(customerId, {
        invoice_settings: { default_payment_method: paymentMethodId },
      });
    }

    // Create the subscription
    const subscriptionParams: Stripe.SubscriptionCreateParams = {
      customer: customerId,
      items: [{ price: priceId }],
      expand: ['latest_invoice.payment_intent'],
    };

    // Add trial period if specified
    if (trialDays) {
      const trialEnd = Math.floor(Date.now() / 1000) + (trialDays * 24 * 60 * 60);
      subscriptionParams.trial_end = trialEnd;
    }

    const subscription = await stripe.subscriptions.create(subscriptionParams);
    return subscription;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
}

// Get a subscription by ID
export async function getSubscription(subscriptionId: string) {
  try {
    return await stripe.subscriptions.retrieve(subscriptionId);
  } catch (error) {
    console.error('Error retrieving subscription:', error);
    throw error;
  }
}

// Update a subscription
export async function updateSubscription(
  subscriptionId: string,
  data: Stripe.SubscriptionUpdateParams
) {
  try {
    return await stripe.subscriptions.update(subscriptionId, data);
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
}

// Cancel a subscription
export async function cancelSubscription(subscriptionId: string, cancelAtPeriodEnd = true) {
  try {
    return await stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: cancelAtPeriodEnd,
    });
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
}

// Change subscription plan
export async function changeSubscriptionPlan(subscriptionId: string, newPriceId: string) {
  try {
    const subscription = await stripe.subscriptions.retrieve(subscriptionId);
    const subscriptionItemId = subscription.items.data[0].id;

    return await stripe.subscriptions.update(subscriptionId, {
      items: [{
        id: subscriptionItemId,
        price: newPriceId,
      }],
    });
  } catch (error) {
    console.error('Error changing subscription plan:', error);
    throw error;
  }
}

/**
 * Payment Method Functions
 */

// Get payment methods for a customer
export async function getPaymentMethods(customerId: string, type: 'card' | 'sepa_debit' | 'us_bank_account' = 'card') {
  try {
    const paymentMethods = await stripe.paymentMethods.list({
      customer: customerId,
      type,
    });
    return paymentMethods.data;
  } catch (error) {
    console.error('Error retrieving payment methods:', error);
    throw error;
  }
}

// Add a new payment method to a customer
export async function addPaymentMethod(customerId: string, paymentMethodId: string, setAsDefault = false) {
  try {
    // Attach payment method to customer
    await stripe.paymentMethods.attach(paymentMethodId, {
      customer: customerId,
    });

    // Set as default if requested
    if (setAsDefault) {
      await stripe.customers.update(customerId, {
        invoice_settings: { default_payment_method: paymentMethodId },
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
}

// Remove a payment method
export async function removePaymentMethod(paymentMethodId: string) {
  try {
    return await stripe.paymentMethods.detach(paymentMethodId);
  } catch (error) {
    console.error('Error removing payment method:', error);
    throw error;
  }
}

// Set default payment method
export async function setDefaultPaymentMethod(customerId: string, paymentMethodId: string) {
  try {
    return await stripe.customers.update(customerId, {
      invoice_settings: { default_payment_method: paymentMethodId },
    });
  } catch (error) {
    console.error('Error setting default payment method:', error);
    throw error;
  }
}

/**
 * Subscription Status Functions
 */

// Check if a subscription is active
export function isSubscriptionActive(status: string) {
  return status === SUBSCRIPTION_STATUS.ACTIVE || status === SUBSCRIPTION_STATUS.TRIALING;
}

// Get all subscriptions for a customer
export async function getCustomerSubscriptions(customerId: string) {
  try {
    const subscriptions = await stripe.subscriptions.list({
      customer: customerId,
      status: 'all',
      expand: ['data.default_payment_method'],
    });
    return subscriptions.data;
  } catch (error) {
    console.error('Error retrieving customer subscriptions:', error);
    throw error;
  }
}

/**
 * Billing and Invoices
 */

// Get invoices for a customer
export async function getCustomerInvoices(customerId: string) {
  try {
    const invoices = await stripe.invoices.list({
      customer: customerId,
    });
    return invoices.data;
  } catch (error) {
    console.error('Error retrieving customer invoices:', error);
    throw error;
  }
}

// Generate a link to a specific invoice
export async function getInvoiceLink(invoiceId: string) {
  try {
    const invoice = await stripe.invoices.retrieve(invoiceId);
    if (invoice.hosted_invoice_url) {
      return invoice.hosted_invoice_url;
    }
    throw new Error('No hosted invoice URL available');
  } catch (error) {
    console.error('Error generating invoice link:', error);
    throw error;
  }
}

/**
 * Checkout Session Functions
 */

// Create a checkout session for subscription signup
export async function createCheckoutSession(
  customerId: string,
  priceId: string,
  successUrl: string,
  cancelUrl: string
) {
  try {
    const session = await stripe.checkout.sessions.create({
      customer: customerId,
      payment_method_types: ['card'],
      line_items: [
        {
          price: priceId,
          quantity: 1,
        },
      ],
      mode: 'subscription',
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return session;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

// Create a portal session for managing billing
export async function createBillingPortalSession(customerId: string, returnUrl: string) {
  try {
    const session = await stripe.billingPortal.sessions.create({
      customer: customerId,
      return_url: returnUrl,
    });
    return session;
  } catch (error) {
    console.error('Error creating billing portal session:', error);
    throw error;
  }
}

/**
 * Webhook Helpers
 */

// Verify Stripe webhook signature
export function constructWebhookEvent(payload: string, signature: string, secret: string) {
  try {
    return stripe.webhooks.constructEvent(payload, signature, secret);
  } catch (error) {
    console.error('Error verifying webhook signature:', error);
    throw error;
  }
}

export default {
  createCustomer,
  getCustomer,
  updateCustomer,
  createSubscription,
  getSubscription,
  updateSubscription,
  cancelSubscription,
  changeSubscriptionPlan,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
  isSubscriptionActive,
  getCustomerSubscriptions,
  getCustomerInvoices,
  getInvoiceLink,
  createCheckoutSession,
  createBillingPortalSession,
  constructWebhookEvent,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_STATUS
}; 